var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import React, { useState, useRef, useCallback, useMemo, } from 'react';
import Downshift from 'downshift';
import styled from '@emotion/styled';
import { display, fontSize, space, height, flex, themeGet, lineHeight, style, } from 'styled-system';
import { Transition } from 'react-transition-group';
import VisuallyHidden from '../../../common/components/VisuallyHidden';
import { useMatchesViewport } from '../../../common/hooks/media';
import { useLocalization } from '../../../common/providers/LocaleProvider';
import { useHiddenBodyOverflow } from '../../../common/hooks/scroll';
import { getTrackerProps } from '../../../common/tracking/getTrackerProps';
import dispatchTrackingEvent from '../../../common/tracking/dispatchTrackingEvent';
import { useSuggestions } from './hooks/suggestions';
import { useSearchLock } from './hooks/search-lock';
import { useSearchQuery } from './hooks/search-query';
import { Img } from './Img';
import { SuggestionsList, SuggestionsListItem } from './SuggestionList';
import { SearchSubmitButton } from './SearchSubmitButton';
import { TransitionedIconButton } from './TransitionedButton';
import SearchFormContainer from './SearchFormContainer';
import SearchInputBorder from './SearchInputBorder';
var TRANSITION_MS = 360;
var pointerEvents = style({
    prop: 'pointerEvents',
    cssProperty: 'pointerEvents',
});
var getSuggestionName = function (suggestion) {
    return suggestion == null ? '' : suggestion.name;
};
var FormWrapper = styled.div(space);
FormWrapper.defaultProps = {
    pt: [3, 0],
};
var Form = styled.form(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  position: relative;\n  display: flex;\n"], ["\n  position: relative;\n  display: flex;\n"])));
var SearchInputContainer = styled.div(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n  position: relative;\n  ", ";\n  ", ";\n  ", ";\n"], ["\n  position: relative;\n  ", ";\n  ", ";\n  ", ";\n"])), display, flex, space);
SearchInputContainer.defaultProps = {
    display: 'flex',
    flex: '1 1 auto',
};
var SearchInputMobileOverlay = styled.div(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n  ", ";\n  position: absolute;\n  width: 100%;\n  height: 100%;\n  top: 0;\n  left: 0;\n"], ["\n  ", ";\n  position: absolute;\n  width: 100%;\n  height: 100%;\n  top: 0;\n  left: 0;\n"])), display);
SearchInputMobileOverlay.defaultProps = {
    display: [null, 'none'],
};
var FakeInputMobile = styled(VisuallyHidden)(templateObject_4 || (templateObject_4 = __makeTemplateObject(["\n  position: fixed;\n  top: 0;\n  /* Prevent iOS autozoom */\n  font-size: 32px;\n"], ["\n  position: fixed;\n  top: 0;\n  /* Prevent iOS autozoom */\n  font-size: 32px;\n"])));
FakeInputMobile.defaultProps = {
    as: 'input',
};
var SearchInput = styled.input(templateObject_5 || (templateObject_5 = __makeTemplateObject(["\n  ", ";\n  ", ";\n  ", ";\n  ", ";\n\n  width: 100%;\n  border: none;\n  outline: none;\n  text-overflow: ellipsis;\n\n  &::placeholder {\n    color: ", ";\n  }\n\n  /* It is really hard to remove browser styles from search field */\n  &[type='search'] {\n    -webkit-appearance: none;\n    /* I hate you, Shop-App */\n    height: auto !important;\n\n    &:invalid {\n      box-shadow: none !important;\n    }\n\n    &::-ms-clear {\n      display: none;\n    }\n\n    &::-webkit-search-decoration,\n    &::-webkit-search-cancel-button,\n    &::-webkit-search-results-button,\n    &::-webkit-search-results-decoration {\n      -webkit-appearance: none;\n    }\n  }\n"], ["\n  ", ";\n  ", ";\n  ", ";\n  ", ";\n\n  width: 100%;\n  border: none;\n  outline: none;\n  text-overflow: ellipsis;\n\n  &::placeholder {\n    color: ", ";\n  }\n\n  /* It is really hard to remove browser styles from search field */\n  &[type='search'] {\n    -webkit-appearance: none;\n    /* I hate you, Shop-App */\n    height: auto !important;\n\n    &:invalid {\n      box-shadow: none !important;\n    }\n\n    &::-ms-clear {\n      display: none;\n    }\n\n    &::-webkit-search-decoration,\n    &::-webkit-search-cancel-button,\n    &::-webkit-search-results-button,\n    &::-webkit-search-results-decoration {\n      -webkit-appearance: none;\n    }\n  }\n"])), fontSize, lineHeight, space, pointerEvents, themeGet('colors.gray.5'));
SearchInput.defaultProps = {
    fontSize: 4,
    lineHeight: 3,
    m: 0,
    // -1px correction for the border size
    py: '7px',
    px: 3,
    pointerEvents: ['none', 'auto'],
};
var SuggestionsListContainer = styled(SuggestionsList)(templateObject_6 || (templateObject_6 = __makeTemplateObject(["\n  ", ";\n"], ["\n  ", ";\n"])), height);
SuggestionsListContainer.defaultProps = __assign(__assign({}, SuggestionsList.defaultProps), { height: ['calc(100% - 68px)', 'auto'] });
var Search = function (_a) {
    var _b = _a.searchDebounce, searchDebounce = _b === void 0 ? 100 : _b, _c = _a.suggestionsLimit, suggestionsLimit = _c === void 0 ? 10 : _c, _d = _a.searchThreshold, searchThreshold = _d === void 0 ? 2 : _d, _e = _a.legacyShopRebootMaxWidth, legacyShopRebootMaxWidth = _e === void 0 ? true : _e;
    var _f = useLocalization(), prefix = _f.prefix, t = _f.t;
    var _g = __read(useMatchesViewport(), 1), matchesTablet = _g[0];
    var _h = __read(useState(''), 2), inputValue = _h[0], setInputValue = _h[1];
    var _j = __read(useState(false), 2), isEditedByUser = _j[0], setIsEditedByUser = _j[1];
    var _k = __read(useState(false), 2), focused = _k[0], setFocused = _k[1];
    var searchContainer = useRef(null);
    var searchPlaceholder = useRef(null);
    var searchInput = useRef(null);
    var fakeInput = useRef(null);
    useSearchQuery(setInputValue);
    var _l = useSuggestions({
        inputValue: inputValue,
        isEditedByUser: isEditedByUser,
        searchDebounce: searchDebounce,
        suggestionsLimit: suggestionsLimit,
        searchThreshold: searchThreshold,
    }), suggestions = _l.suggestions, clearSuggestions = _l.clearSuggestions, searchClientActionPath = _l.searchClientActionPath;
    var _m = useSearchLock({
        searchContainer: searchContainer,
        searchPlaceholder: searchPlaceholder,
        setInputValue: setInputValue,
    }), isLocked = _m.isLocked, lockSearch = _m.lockSearch, unlockSearch = _m.unlockSearch;
    var isMobile = !matchesTablet;
    useHiddenBodyOverflow(focused && isMobile, '[data-testid="suggestion-list"]');
    var onInputValueChange = useCallback(function (event) {
        setInputValue(event.target.value);
        setIsEditedByUser(true);
    }, []);
    var opened = useMemo(function () { return inputValue.trim().length >= searchThreshold && suggestions.length > 0; }, [inputValue, searchThreshold, suggestions]);
    var isClearButtonHidden = useMemo(function () { return inputValue.trim().length === 0; }, [
        inputValue,
    ]);
    var onSelect = useCallback(function (selectedItem) {
        if (selectedItem && selectedItem.urlEncoded) {
            dispatchTrackingEvent({
                event: 'internal search',
                eventAction: 'click',
                eventCategory: 'link',
                eventData: {
                    label: selectedItem.name,
                    listIndex: suggestions.findIndex(function (item) { return item === selectedItem; }),
                    listItems: suggestions.map(function (item) { return item.name; }),
                    listLength: suggestions.length,
                    searchTerm: inputValue,
                    targetUrl: searchClientActionPath + "?query=" + inputValue,
                },
            });
            window.location.href = selectedItem.urlEncoded;
        }
    }, []);
    var onStateChange = useCallback(function (changes) {
        // If the "Esc" key is pressed, "unlock" the screen
        if (changes.type === Downshift.stateChangeTypes.keyDownEscape) {
            unlockSearch();
        }
    }, []);
    return (React.createElement(Transition, { in: isLocked, timeout: TRANSITION_MS, onEnter: function () {
            // After we set the top in `lockSearch` callback remove
            // the top value from styles to start "entering" animation
            searchContainer.current.style.top = null;
        }, onEntered: function () {
            // After the animation ended, move focus to the real input
            // field (iOS keyboard is already on screen, everything is fine)
            searchInput.current.focus();
        }, onExited: function () {
            // After exit animation, clean inline styles
            searchContainer.current.style.willChange = null;
            searchContainer.current.style.top = null;
        } }, function (lockedTransitionState) { return (React.createElement(Downshift, { inputValue: inputValue, itemToString: getSuggestionName, isOpen: opened, onSelect: onSelect, onStateChange: onStateChange }, function (_a) {
        var getRootProps = _a.getRootProps, getInputProps = _a.getInputProps, getItemProps = _a.getItemProps, getLabelProps = _a.getLabelProps, getMenuProps = _a.getMenuProps, highlightedIndex = _a.highlightedIndex, isOpen = _a.isOpen;
        var rootProps = getRootProps({
            onFocus: function () {
                setFocused(true);
            },
            onBlur: function () {
                setFocused(false);
            },
            // Form container animation is half the time of the whole
            // animation cycle. We sync a lot of things with the same timeout
            // allowing to show the form first, before animating buttons
            timeout: TRANSITION_MS / 2,
            transitionState: lockedTransitionState,
            ref: searchContainer,
            'data-testid': 'search-container',
        }, 
        // Downshift doesn't recognize that the ref is passed correctly
        // https://github.com/downshift-js/downshift#getrootprops
        { suppressRefError: true });
        var formProps = getTrackerProps({
            action: searchClientActionPath,
            onClick: function (event) {
                // Prevent default click tracking on the form. This
                // will allow to push event only when it is coming from
                // submit event
                event.nativeEvent.trackingPreventDefault = true;
            },
            locked: isLocked,
        }, {
            event: 'internal search',
            eventData: {
                placement: 'header',
                searchTerm: inputValue,
                targetUrl: searchClientActionPath + "?query=" + inputValue,
            },
        });
        var inputProps = getInputProps({
            name: 'query',
            type: 'text',
            required: true,
            placeholder: t('h24_search_product'),
            onChange: onInputValueChange,
            ref: searchInput,
            'data-testid': 'search-bar-input',
            pointerEvents: isLocked ? 'auto' : undefined,
        });
        var menuProps = getMenuProps({
            isOpen: isOpen,
            timeout: TRANSITION_MS / 2,
            'data-testid': 'suggestion-list',
        });
        return (React.createElement(React.Fragment, null,
            React.createElement(SearchFormContainer, __assign({}, rootProps),
                React.createElement(FormWrapper
                // Adjust paddings when locked
                , { 
                    // Adjust paddings when locked
                    px: lockedTransitionState === 'exited' ? 0 : 3, pb: lockedTransitionState === 'exited' ? 0 : '12px' },
                    React.createElement(Form, __assign({}, formProps),
                        React.createElement(TransitionedIconButton, { type: "button", "data-testid": "search-back-button", onClick: function () {
                                // It is only possible to unlock search modal
                                // with the button
                                // https://home24.atlassian.net/browse/CNN-597
                                unlockSearch();
                            }, timeout: TRANSITION_MS, 
                            // Back button will appear with delay after SearchFormContainer
                            // finished animating, but will disappear first
                            delay: TRANSITION_MS / 2, hidden: !isLocked, "aria-hidden": !isLocked, tabIndex: !isLocked ? -1 : 0, hideToLeft: true, mr: 2 },
                            React.createElement(VisuallyHidden, null, t('h24_back')),
                            React.createElement(Img, { name: "arrowLeft", dataTestId: "back-button-icon", title: t('h24_back') })),
                        React.createElement(VisuallyHidden, __assign({}, getLabelProps({ as: 'label' })), t('h24_search_product')),
                        React.createElement(SearchInputBorder, { locked: isLocked, focused: focused, timeout: TRANSITION_MS / 2 },
                            React.createElement(SearchInputContainer
                            // Adjusting container size when the
                            // clear button is hidden
                            , { 
                                // Adjusting container size when the
                                // clear button is hidden
                                mr: isClearButtonHidden ? '-32px' : 0 },
                                !isLocked && (
                                // This overlay will only be visible on mobile
                                // and only when the form is not "locked". We need
                                // it to work around iOS bugs with screen auto scroll
                                // when focusing on the inputs that are not completely
                                // in the upper part of the page.
                                //
                                // This overlay will trigger focus on the FakeInputMobile
                                // that is always on the top of the page, and this fake
                                // input will start the lock screen process
                                React.createElement(SearchInputMobileOverlay, { "data-testid": "search-overlay", onClick: function () {
                                        // Triggering focus immediately on click allows
                                        // for iOS keyboard to appear on the screen
                                        fakeInput.current.focus();
                                    }, onFocus: function () {
                                        // Same as above
                                        fakeInput.current.focus();
                                    }, tabIndex: 0 })),
                                React.createElement(SearchInput, __assign({}, inputProps))),
                            React.createElement(TransitionedIconButton, { type: "button", "data-testid": "search-clear-button", onClick: function () {
                                    // On clear button interaction, clean input and focus back to the input
                                    // so users can start typing immediately
                                    setInputValue('');
                                    if (searchInput.current) {
                                        searchInput.current.focus();
                                    }
                                }, hidden: isClearButtonHidden, "aria-hidden": isClearButtonHidden, tabIndex: isClearButtonHidden ? -1 : 0, timeout: TRANSITION_MS / 2, hideToRight: true },
                                React.createElement(VisuallyHidden, null, t('h24_clear_search')),
                                React.createElement(Img, { name: "clear", dataTestId: "clear-search-icon", title: t('h24_clear_search') }))),
                        React.createElement(SearchSubmitButton, { type: "submit", timeout: TRANSITION_MS, transitionState: lockedTransitionState }, t('h24_search_button_text')))),
                React.createElement(Transition, { in: isOpen, timeout: TRANSITION_MS / 2, 
                    // We clear the suggestions state on animation exit
                    // so the content is properly animated without jumping
                    // around. If we do this before, there will be no rendered
                    // items to animate
                    onExited: clearSuggestions },
                    React.createElement(SuggestionsListContainer, __assign({ maxWidth: legacyShopRebootMaxWidth
                            ? [null, '388px']
                            : [null, '388px', null, '480px'] }, menuProps), suggestions.map(function (item, index) {
                        item = __assign(__assign({}, item), { url: prefix(item.url), urlEncoded: prefix(item.urlEncoded) });
                        return (React.createElement(SuggestionsListItem, __assign({ key: item.name }, getItemProps({
                            item: item,
                            index: index,
                            // Downshift "swallows" item from the props,
                            // we have to pass it with different key
                            suggestionItem: item,
                            highlighted: highlightedIndex === index,
                            inputValue: inputValue,
                            mobile: isMobile,
                            // Tracking data
                            listIndex: index,
                            listItems: suggestions.map(function (el) { return el.name; }),
                        })), item.name));
                    })))),
            React.createElement("div", { "aria-hidden": "true", ref: searchPlaceholder, style: {
                    display: lockedTransitionState === 'exited' ? 'none' : 'block',
                    // Search height 40px + padding top 16px, we can do some fancy calculations for
                    // this but eh...
                    height: '56px',
                } }),
            isMobile && (React.createElement("label", { htmlFor: "search-box-input-focus-helper" },
                React.createElement(FakeInputMobile, { ref: fakeInput, onFocus: lockSearch, id: "search-box-input-focus-helper", "aria-label": inputProps.placeholder, "data-testid": "search-box-input-focus-helper" })))));
    })); }));
};
export default Search;
var templateObject_1, templateObject_2, templateObject_3, templateObject_4, templateObject_5, templateObject_6;
